import Phaser from 'phaser';
import GoldButton from 'ui/button';
import { drawUIBackground } from 'ui/dialogue';
import {
  COLORS,
  RED_PIECES,
  RED_PIECE_RANK_INDEXES,
  BLUE_PIECES
} from 'helpers/constants';
import SpriteButton from 'ui/spriteButton';
import { undoLastMove } from 'helpers/historyHelpers';
import * as Movement from 'helpers/movementHelpers';

const MENU_TIMEOUT = 3000;

const getCapturedPieceIndex = piece =>
  piece && typeof piece === 'object' ? piece.index : piece;

const getMiniPieceFrame = piece => {
  const pieceIndex = getCapturedPieceIndex(piece);
  const redPieceIndex = RED_PIECES.indexOf(pieceIndex);
  if (redPieceIndex > -1) {
    return RED_PIECE_RANK_INDEXES[redPieceIndex] + 6;
  }
  const bluePieceIndex = BLUE_PIECES.indexOf(pieceIndex);
  if (bluePieceIndex > -1) {
    return bluePieceIndex % 6;
  }
  return 0;
};

export default class GameMenuScene extends Phaser.Scene {
  constructor() {
    super('GameMenuScene');

    this.showAll = this.showAll.bind(this);
    this.showObjectiveOnly = this.showObjectiveOnly.bind(this);
    this.hideAll = this.hideAll.bind(this);
    this.showKeyItem = this.showKeyItem.bind(this);
    this.hideKeyItem = this.hideKeyItem.bind(this);
    this.showBattleStart = this.showBattleStart.bind(this);

    window.titleShowAll = this.showAll;
    window.titleShowObjective = this.showObjectiveOnly;
    window.titleHideAll = this.hideAll;
  }
  init(data) {
    this.data = data;
    this.scale.on('resize', this.resize);
    this.events.on('shutdown', () => {
      this.scale.off('resize', this.resize);
      this.keyShown = false;
    });
  }
  resize = (gameSize, baseSize, displaySize, previousWidth, previousHeight) => {
    const scale = this.game.defaultZoom;
    const { centerX } = this.cameras.main;
    this.titleBG
      .setPosition(centerX, this.titleBG.originalY - 42 * scale)
      .setScale(scale);
    this.titleLabel.originalY = 31 * scale;
    this.titleLabel
      .setPosition(centerX - 6, this.titleLabel.originalY - 42 * scale)
      .setStyle({ fontSize: `${scale * 16}px` });
    this.objectiveLabel.originalY = 56 * scale;
    this.objectiveLabel
      .setPosition(
        this.cameras.main.centerX,
        this.objectiveLabel.originalY - 42 * scale
      )
      .setStyle({ fontSize: `${scale * 8}px` });
    //Turn Indicator
    this.objectiveUpdated
      .setPosition(this.game.canvas.width - 4 * scale, 4 * scale)
      .setScale(scale);
    this.turnBG
      .setPosition(this.game.canvas.width, this.game.canvas.height)
      .setScale(scale);
    this.turnFlags
      .setPosition(
        this.game.canvas.width - 98 * scale,
        this.game.canvas.height - 28 * scale
      )
      .setScale(scale);
    this.turnLabel
      .setPosition(
        this.game.canvas.width - 56 * scale,
        this.game.canvas.height - 28 * scale
      )
      .setStyle({
        fontSize: `${scale * 24}px`
      });
    this.turnNumberLabel
      .setPosition(
        this.game.canvas.width - 18 * scale,
        this.game.canvas.height - 29 * scale
      )
      .setStyle({
        fontSize: `${scale * 28}px`
      });
    this.keyItemBG
      .setPosition(
        this.keyShown ? 0 : -(85 * scale),
        this.game.canvas.height - 8 * scale
      )
      .setScale(scale);
    this.player1
      .setPosition(this.game.canvas.width - 10 * scale, 10 * scale)
      .setStyle({
        fontSize: `${scale * 24}px`
      });

    this.player2
      .setPosition(this.game.canvas.width - 10 * scale, 70 * scale)
      .setStyle({
        fontSize: `${scale * 24}px`
      });

    this.battleStartGfx.setPosition(centerX, -300 * scale).setScale(scale);
    this.battleResults
      .setScale(scale)
      .setPosition(this.cameras.main.centerX, this.cameras.main.centerY);
  };
  create = () => {
    this.onReadyCalledNum = 0;
    this.capturedRedGroup = this.add.group();
    this.capturedBlueGroup = this.add.group();

    const data = this.data;
    this.requiredReadyCalls = data.sceneRef.sharedLevelMode ? 1 : 2;
    const hasObjective = data.level && data.level.objective;
    const scale = this.game.defaultZoom;
    // this.scene.backgroundColor = 0x1f272c;
    let titleGraphicSuffix = 'overworld';
    if (data.level && data.level.decorType) {
      titleGraphicSuffix = data.level.decorType;
      if (!hasObjective) {
        titleGraphicSuffix += '_no_obj';
      }
    } else if (data.sceneRef.currentBiome) {
      titleGraphicSuffix = data.sceneRef.currentBiome.title_indicator_sprite;
    }
    this.turnNumber = 0;
    this.titleBG = this.add
      .sprite(this.cameras.main.centerX, 0, 'ui_title_' + titleGraphicSuffix)
      .setScale(scale)
      .setDepth(8)
      .setOrigin(0.5, 0)
      .setInteractive({ pixelPerfect: true })
      .setAlpha(1);
    this.titleBG.originalY = 0;
    this.titleBG.on('pointerover', () => {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
      }
      this.showAll();
    });
    this.titleBG.on('pointerout', () => {
      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
      }
      this.hideTimer = setTimeout(this.showObjectiveOnly, MENU_TIMEOUT);
    });
    let title =
      (data.level && data.level.name && data.level.name.replace(/-/g, ' ')) ||
      'unknown ';
    if (data.sceneRef.scene.key === 'WorldScene') {
      title = data.sceneRef.currentBiome.name;
    }
    let titleLabelY = 31 * scale;
    this.titleLabel = this.add
      .text(this.cameras.main.centerX - 6, titleLabelY, ` ${title}`, {
        fontSize: `${scale * 16}px`,
        fontFamily: 'Astalemtim',
        color: COLORS.LIGHT_YELLOW,
        align: 'center'
      })
      .setDepth(8)
      .setResolution(8)
      .setOrigin(0.5);
    this.titleLabel.originalY = titleLabelY;
    let objectiveText = hasObjective;
    let objectiveLabelY = 56 * scale;
    const yellowDecor = ['home', 'forest'].includes(data.level.decorType);
    const yellowBiome =
      data.sceneRef.currentBiome &&
      data.sceneRef.currentBiome.name === 'Fickle Woods';
    const objectiveColor =
      yellowDecor || yellowBiome ? COLORS.LIGHT_YELLOW : COLORS.LIGHT_BLUE;
    this.objectiveLabel = this.add
      .text(this.cameras.main.centerX, objectiveLabelY, `\n${objectiveText}`, {
        fontSize: `${scale * 8}px`,
        fontFamily: 'pixelMix',
        color: objectiveColor,
        align: 'center'
      })
      .setDepth(8)
      .setResolution(8)
      .setOrigin(0.5)
      .setAlpha(hasObjective ? 1 : 0);
    this.objectiveLabel.originalY = objectiveLabelY;
    this.titleGroup = this.add.group();
    this.titleGroup.add(this.titleBG);
    this.titleGroup.add(this.titleLabel);
    this.titleGroup.add(this.objectiveLabel);

    //Main Menu
    if (!this.data.sceneRef.sharedLevelMode) {
      this.scene.launch('OptionsMainMenuScene', {
        sceneRef: this.data.sceneRef,
        onReady: this.onOptionsOrMenuReady
      });
    }

    //objective updated
    this.objectiveUpdated = this.add
      .image(
        this.game.canvas.width - 4 * scale,
        4 * scale,
        'ui_objective_updated'
      )
      .setScale(scale)
      .setOrigin(1, 0)
      .setAlpha(0);

    //Turn Indicator
    this.turnBG = this.add
      .sprite(this.game.canvas.width, this.game.canvas.height, 'ui_turns')
      .setOrigin(1)
      .setScale(scale)
      .setAlpha(0);
    this.turnFlags = this.add
      .sprite(
        this.game.canvas.width - 98 * scale,
        this.game.canvas.height - 28 * scale,
        'ui_flags',
        0
      )
      .setOrigin(0.5)
      .setScale(scale)
      .setAlpha(0);
    this.turnLabel = this.add
      .text(
        this.game.canvas.width - 56 * scale,
        this.game.canvas.height - 28 * scale,
        `TURN`,
        {
          fontSize: `${scale * 24}px`,
          fontFamily: 'pixelOperatorBold',
          color: COLORS.LIGHT_BLUE
        }
      )
      .setOrigin(0.5)
      .setResolution(8);
    this.turnNumberLabel = this.add
      .text(
        this.game.canvas.width - 18 * scale,
        this.game.canvas.height - 29 * scale,
        this.turnNumber,
        {
          fontSize: `${scale * 28}px`,
          fontFamily: 'pixelOperatorBold',
          color: COLORS.LIGHT_YELLOW,
          align: 'center'
        }
      )
      .setOrigin(0.5)
      .setResolution(8);

    this.undoButton = SpriteButton(this, {
      calcX: scene => scene.game.canvas.width,
      calcY: scene => scene.game.canvas.height - 46 * scene.game.defaultZoom,
      sprite: 'ui_undo_last_turn',
      action: () => {
        this.data.sceneRef.disableInput = true;
        this.data.sceneRef.scene.launch('DialogueScene', {
          text: `Undo last move? Are you sure?`,
          scene: this.data.sceneRef,
          options: [
            {
              label: 'Yes, undo last move.',
              action: this.undoLastMove
            },
            {
              label: 'No, cancel.',
              action: () => {}
            }
          ],
          end: () => {
            this.data.sceneRef.scene.stop('DialogueScene');
            this.data.sceneRef.disableInput = false;
          }
        });
      }
    })
      .setOrigin(1)
      .setAlpha(0);

    // Player Info
    this.playerNamesGroup = this.add.group();
    this.player1 = this.add
      .text(this.game.canvas.width - 10 * scale, 10 * scale, `{} - P1`, {
        fontSize: `${scale * 24}px`,
        fontFamily: 'pixelOperatorBold',
        color: COLORS.PIECE_MID_BLUE
      })
      .setOrigin(1, 0.5)
      .setResolution(8)
      .setAlpha(0);

    this.player2 = this.add
      .text(this.game.canvas.width - 10 * scale, 70 * scale, `{} - P2`, {
        fontSize: `${scale * 24}px`,
        fontFamily: 'pixelOperator',
        color: COLORS.PIECE_MID_RED
      })
      .setOrigin(1, 0.5)
      .setResolution(8)
      .setAlpha(0);

    this.playerNamesGroup.add(this.player1);
    this.playerNamesGroup.add(this.player2);

    //Key Item indicator
    this.keyItemBG = this.add
      .sprite(
        -(85 * scale),
        this.game.canvas.height - 8 * scale,
        'ui_item_indicator'
      )
      .setScale(scale)
      .setOrigin(0, 1);

    this.battleStartGfx = this.add
      .sprite(this.cameras.main.centerX, -300 * scale, 'ui_battle_start')
      .setScale(scale)
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(12);

    this.battleResults = this.add
      .container()
      .setDepth(12)
      .setAlpha(0)
      .setScale(scale)
      .setPosition(this.cameras.main.centerX, this.cameras.main.centerY);

    this.battleResultsBG = this.add
      .sprite(0, 0, 'ui_battle_results')
      .setOrigin(0.5);
    this.battleResultsWin = this.add
      .sprite(-6, 10, 'ui_battle_win')
      .setAlpha(1)
      .setOrigin(0.5);
    this.battleResultsLose = this.add
      .sprite(-10, 10, 'ui_battle_lose')
      .setAlpha(0)
      .setOrigin(0.5);
    this.battleResultsDraw = this.add
      .sprite(-10, 10, 'ui_battle_draw')
      .setAlpha(0)
      .setOrigin(0.5);
    this.battleResultsText = this.add
      .text(
        -5,
        50,
        `\nThere are no more turns left.\nYou've taken more pieces.`,
        {
          fontSize: `8px`,
          fontFamily: 'pixelMix',
          color: COLORS.LIGHT_BLUE,
          align: 'center'
        }
      )
      .setResolution(8)
      .setOrigin(0.5);
    this.returnToLobbyBG = drawUIBackground(
      this,
      { x: -67, y: 74, width: 132, height: 24 },
      1
    );

    this.returnToLobby = GoldButton(this, {
      text: 'Return To Lobby',
      x: -4,
      y: 82,
      width: 121,
      height: 18,
      scaleMultiplier: 1 / scale,
      action: () => {
        this.data.sceneRef.leaveMultiplayerGame?.('OnlineMenuScene', {
          notifyServer: false
        });
      }
    });

    this.battleResultsCapturedBG = this.add
      .sprite(-6, -36, 'ui_battle_captured')
      .setAlpha(1)
      .setOrigin(0.5);

    this.battleResults.add(this.battleResultsBG);
    this.battleResults.add(this.battleResultsWin);
    this.battleResults.add(this.battleResultsLose);
    this.battleResults.add(this.battleResultsDraw);
    this.battleResults.add(this.battleResultsText);
    this.battleResults.add(this.returnToLobbyBG);
    this.battleResults.add(this.returnToLobby);
    this.battleResults.add(this.battleResultsCapturedBG);

    if (data.level.combat || data.multiplayer) {
      this.showTurnIndicator();
    } else {
      this.hideTurnIndicator();
    }

    if (data.hideObjective) {
      this.hideObjective();
    }

    this.scene.bringToTop();

    //debug keys
    // var hideAllKey = this.input.keyboard.addKey('A');
    // hideAllKey.on('up', this.hideAll);
    // var showObjectiveKey = this.input.keyboard.addKey('S');
    // showObjectiveKey.on('up', this.showObjectiveOnly);
    // var showAllKey = this.input.keyboard.addKey('D');
    // showAllKey.on('up', this.showAll);

    this.hideTimer = setTimeout(this.showObjectiveOnly, MENU_TIMEOUT);
    if (data.onReady) {
      this.onOptionsOrMenuReady();
    }

    // debug controller axes input
    this.scene.launch('DebugControllerScene', { scene: this });
  };

  showObjectiveUpdated = () => {
    this.objectiveUpdated.setAlpha(1);
    setTimeout(() => {
      if (this.data.sceneRef.scene.isActive()) {
        this.tweens.add({
          targets: this.objectiveUpdated,
          alpha: 0,
          ease: 'Expo.easeOut',
          speed: 1500
        });
      }
    }, 3000);
  };

  onOptionsOrMenuReady = () => {
    this.onReadyCalledNum += 1;
    if (
      this.data.onReady &&
      this.onReadyCalledNum >= (this.requiredReadyCalls || 2)
    ) {
      this.data.onReady();
    }
  };

  undoLastMove = cb => {
    let [history_colors] = this.data.sceneRef.game.registry.get([
      'history_colors'
    ]);
    let next;
    for (let i = history_colors.length - 1; i > -1; i--) {
      if (history_colors[i] === 'blue') {
        next = i - 1;
        break;
      }
    }
    this.data.sceneRef.captured.blue = this.data.sceneRef.captured.blue.filter(
      blueCaptured => blueCaptured.turnIndex < next
    );
    this.data.sceneRef.captured.red = this.data.sceneRef.captured.red.filter(
      redCaptured => redCaptured.turnIndex < next
    );
    undoLastMove(this.data.sceneRef, () => {
      const menuScene = this.data.sceneRef.scene.get('GameMenuScene');
      const [historyIndex] = this.data.sceneRef.game.registry.get([
        'history_index'
      ]);
      menuScene.setTurnNumber(historyIndex + 1);
      this.data.sceneRef.updatePieceSprites();
      this.data.sceneRef.numUndosUsed += 1;
      // reselect the only piece, or none like when starting battle
      // might be better to select who moved that move, but I haven't figured out the logic for that yet
      const pieces = this.data.sceneRef.pieces.getChildren();
      const bluePieces = pieces.filter(piece => piece.color === 'blue');
      const defaultFirstPiece = bluePieces[0];
      // console.log('reselect last piece:', bluePieces, defaultFirstPiece);
      if (bluePieces.length === 1 && defaultFirstPiece) {
        this.data.sceneRef.disableInput = false;
        this.data.sceneRef.pickupUnit(defaultFirstPiece);
      } else {
        this.data.sceneRef.hideAbilityButton();
        this.data.sceneRef.selectedArrow &&
          this.data.sceneRef.selectedArrow.setAlpha(0);
        this.data.sceneRef.currentSelectedPiece = null;
        Movement.clearTileHighlighting(this.data.sceneRef.map);
        Movement.setTilesSelectable(
          this.data.sceneRef.pieces,
          this.data.sceneRef.map
        );
      }
      if (cb) {
        cb();
      }
    });
  };

  setRetreatTimer() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer);
    }
    this.hideTimer = setTimeout(this.showObjectiveOnly, MENU_TIMEOUT);
  }

  showBattleResults = (win, draw, text) => {
    if (draw) {
      this.battleResultsWin.setAlpha(0);
      this.battleResultsLose.setAlpha(0);
      this.battleResultsDraw.setAlpha(1);
    } else {
      if (win) {
        this.battleResultsWin.setAlpha(1);
        this.battleResultsLose.setAlpha(0);
        this.battleResultsDraw.setAlpha(0);
      } else {
        this.battleResultsWin.setAlpha(0);
        this.battleResultsLose.setAlpha(1);
        this.battleResultsDraw.setAlpha(0);
      }
    }
    // place red captured pieces
    this.data.sceneRef.captured.red.forEach((piece, index) => {
      const baseX = this.isPlayer1 ? 60 : -64;
      const baseY = -36;
      const modifier = this.isPlayer1 ? -10 : 10;
      const sprite = this.add.sprite(
        baseX + modifier * index,
        baseY,
        'ui_mini_pieces',
        getMiniPieceFrame(piece)
      );
      this.battleResults.add(sprite);
    });
    // place blue capured pieces
    this.data.sceneRef.captured.blue.forEach((piece, index) => {
      const baseX = this.isPlayer1 ? -64 : 60;
      const baseY = -36;
      const modifier = this.isPlayer1 ? 10 : -10;
      const sprite = this.add.sprite(
        baseX + modifier * index,
        baseY,
        'ui_mini_pieces',
        getMiniPieceFrame(piece)
      );
      this.battleResults.add(sprite);
    });
    this.battleResultsText.setText(`\n${text}`);
    this.tweens.add({
      targets: this.battleResults,
      alpha: 1,
      ease: 'Expo.easeOut',
      speed: 1500
    });
  };

  showBattleStart(done) {
    this.battleStartGfx.y = -this.battleStartGfx.height;
    this.battleStartGfx.alpha = 1;
    this.battleStartGfx.scale = this.game.defaultZoom;
    this.tweens.add({
      targets: this.battleStartGfx,
      y: this.cameras.main.centerY,
      duration: 850,
      ease: 'Bounce.easeOut',
      onComplete: () => {
        setTimeout(() => {
          this.tweens.add({
            targets: this.battleStartGfx,
            scale: 0,
            duration: 850,
            ease: 'Back.easeIn',
            onComplete: () => {
              this.battleStartGfx.y = -this.battleStartGfx.height;
              done();
            }
          });
        }, 650);
      }
    });
  }
  setObjectiveColor(color) {
    if (color !== this.objectiveLabel.style.color) {
      this.objectiveLabel.setStyle({ color });
      this.tweens.add({
        targets: this.objectiveLabel,
        y: this.objectiveLabel.y + 20 * this.game.defaultZoom,
        scale: 5,
        ease: 'Expo.easeOut',
        speed: 650,
        yoyo: true,
        hold: 250
      });
      this.tweens.add({
        targets: [this.turnNumberLabel],
        y: `-=${50 * this.game.defaultZoom}`,
        x: `-=${50 * this.game.defaultZoom}`,
        scale: 5,
        ease: 'Expo.easeOut',
        speed: 650,
        yoyo: true,
        hold: 250
      });
    }
  }

  setTurnColor(color) {
    this.turnColor = color;
    this.turnFlags && this.turnFlags.setFrame(color === 'blue' ? 0 : 1);
  }
  setTurnNumber(number) {
    this.turnNumber = number;
    if (
      this.turnNumberLabel &&
      this.turnNumberLabel.scene &&
      this.turnNumberLabel.frame
    ) {
      this.turnNumberLabel.text = this.turnNumber;
    }
  }
  showTurnIndicator() {
    this.turnLabel.alpha = 1;
    this.turnBG.alpha = 1;
    this.turnFlags.alpha = 1;
    this.turnNumberLabel.alpha = 1;
  }
  hideTurnIndicator() {
    this.turnLabel.alpha = 0;
    this.turnBG.alpha = 0;
    this.turnFlags.alpha = 0;
    this.turnNumberLabel.alpha = 0;
    this.undoButton.alpha = 0;
  }
  showAll() {
    this.titleGroup.getChildren().forEach(member => {
      this.tweens.add({
        targets: member,
        y: member.originalY,
        ease: 'Quad.easeIn',
        duration: 350
      });
    });
  }
  showObjectiveOnly() {
    // timing issue causing to call children accessor when none exist and Phaser doesn't check for null-refs :(
    if (!this.titleGroup.children?.entries) {
      return;
    }

    this.titleGroup.getChildren().forEach(member => {
      this.tweens.add({
        targets: member,
        y: member.originalY - 42 * this.game.defaultZoom,
        ease: 'Quad.easeInOUt',
        duration: 850
      });
    });
  }
  hideAll() {
    this.titleGroup.getChildren().forEach(member => {
      this.tweens.add({
        targets: member,
        y: member.originalY - 68 * this.game.defaultZoom,
        ease: 'Quad.easeIn',
        duration: 350
      });
    });
  }
  hideObjective() {
    if (!this.titleBG.texture.key.includes('_no_obj')) {
      this.titleBG.setTexture(this.titleBG.texture.key + '_no_obj');
    }
    this.objectiveLabel.setAlpha(0);
  }
  showObjective() {
    this.titleBG.setTexture(this.titleBG.texture.key.replace('_no_obj', ''));
    this.objectiveLabel.setAlpha(1);
  }
  setObjectiveText = text => {
    if (this.objectiveLabel) {
      this.objectiveLabel.text = `\n${text}`;
      const yellowDecor = ['home', 'forest'].includes(
        this.data.level.decorType
      );
      const yellowBiome =
        this.data.sceneRef.currentBiome &&
        this.data.sceneRef.currentBiome.name === 'Fickle Woods';
      const objectiveColor =
        yellowDecor || yellowBiome ? COLORS.LIGHT_YELLOW : COLORS.LIGHT_BLUE;
      this.objectiveLabel.setStyle({ color: objectiveColor });
    }
  };
  showPlayerNames = (opponent, first) => {
    const currentPlayer = this.game.player.name;
    this.isPlayer1 = first;
    if (first) {
      this.player1.setText(`${currentPlayer} - P1`);
      this.player2.setText(`${opponent.name} - P2`);
      this.player1.setStyle({ color: COLORS.PIECE_MID_BLUE });
      this.player2.setStyle({ color: COLORS.PIECE_MID_RED });
    } else {
      this.player1.setText(`${opponent.name} - P1`);
      this.player2.setText(`${currentPlayer} - P2`);
      this.player1.setStyle({ color: COLORS.PIECE_MID_RED });
      this.player2.setStyle({ color: COLORS.PIECE_MID_BLUE });
    }
    this.playerNamesGroup.getChildren().forEach(child => {
      child.setAlpha(1);
    });
  };
  hidePlayerNames = () => {
    this.playerNamesGroup.getChildren().forEach(child => {
      child.setAlpha(0);
    });
  };
  setPlayerTurn = color => {
    if (
      (color === 'blue' && this.isPlayer1) ||
      (color === 'red' && !this.isPlayer1)
    ) {
      this.player1.setStyle({ fontFamily: 'pixelOperatorBold' });
      this.player2.setStyle({ fontFamily: 'pixelOperator' });
    } else {
      this.player2.setStyle({ fontFamily: 'pixelOperatorBold' });
      this.player1.setStyle({ fontFamily: 'pixelOperator' });
    }
  };
  setTitleText(text) {
    this.titleLabel.text = text;
  }
  setWorldBiomeTitle(biome) {
    if (!biome || !this.titleBG || !this.titleLabel) {
      return;
    }

    const hideObjective = this.objectiveLabel?.alpha === 0;
    const textureKey = `ui_title_${biome.title_indicator_sprite}${
      hideObjective ? '_no_obj' : ''
    }`;
    if (this.textures.exists(textureKey)) {
      this.titleBG.setTexture(textureKey);
    }
    this.setTitleText(biome.name);
    this.showAll();
    this.setRetreatTimer();
  }
  showKeyItem() {
    this.keyShown = true;
    this.tweens.add({
      targets: this.keyItemBG,
      x: 0,
      duration: 550,
      ease: 'Quad.easeIn'
    });
  }
  hideKeyItem() {
    this.keyShown = false;
    this.tweens.add({
      targets: this.keyItemBG,
      x: -(85 * this.game.defaultZoom),
      duration: 350,
      ease: 'Quad.easeIn'
    });
  }
  updateCaptured() {
    this.renderCaptured();
  }
  renderCaptured() {
    this.capturedRedGroup.clear(true, true);
    this.capturedBlueGroup.clear(true, true);
    const scale = this.game.defaultZoom;
    // place red captured pieces
    this.data.sceneRef.captured.red.forEach((piece, index) => {
      const baseX = this.game.canvas.width - 15 * scale;
      const baseY = this.isPlayer1 ? 30 * scale : 90 * scale;
      const sprite = this.add
        .sprite(
          baseX - 10 * scale * index,
          baseY,
          'ui_mini_pieces',
          getMiniPieceFrame(piece)
        )
        .setScale(scale);
      this.capturedRedGroup.add(sprite);
    });
    // place blue capured pieces
    this.data.sceneRef.captured.blue.forEach((piece, index) => {
      const baseX = this.game.canvas.width - 15 * scale;
      const baseY = this.isPlayer1 ? 90 * scale : 30 * scale;
      const sprite = this.add
        .sprite(
          baseX - 10 * scale * index,
          baseY,
          'ui_mini_pieces',
          getMiniPieceFrame(piece)
        )
        .setScale(scale);
      this.capturedBlueGroup.add(sprite);
    });
  }

  update() {
    let historyIndex = this.game.registry.get('history_index');
    if (!historyIndex) {
      this.turnNumber = 1;
      if (
        this.turnNumberLabel &&
        this.turnNumberLabel.scene &&
        this.turnNumberLabel.frame &&
        this.turnNumberLabel.text !== this.turnNumber
      ) {
        this.turnNumberLabel.text = this.turnNumber;
      }
    }
    // undo button
    const inCombatInWorld =
      this.data.sceneRef.inBattleLevelData &&
      this.data.sceneRef.inBattleLevelData.combat &&
      this.data.sceneRef.battlingInWorld;
    const inCombatInStoryLevel = this.data.level && this.data.level.combat;
    const gameOverScreenShowing =
      this.data.sceneRef?.gameOverContainer?.alpha > 0;
    const inCustscene = this.data.sceneRef.inCustscene;
    if (
      (inCombatInWorld || inCombatInStoryLevel) &&
      !gameOverScreenShowing &&
      !inCustscene
    ) {
      const dialogueScene = this.data.sceneRef.scene.get('DialogueScene');
      const dialogueActive = dialogueScene.scene.isActive();
      if (dialogueActive) {
        this.undoButton.alpha = 0;
      } else {
        if (this.turnNumber > 2 && !this.undoButton.alpha) {
          this.undoButton.alpha = 1;
        } else if (this.turnNumber < 3 && this.undoButton.alpha) {
          this.undoButton.alpha = 0;
        }
      }
    } else {
      this.undoButton.alpha = 0;
    }
  }
}
